import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../bizsuite-app';
import { toast } from '../../utils/toast';

/**
 * LushaSettings — admin tab for the Lusha integration.
 * Lives under Integrations as one of the tabbed services. Reads/writes
 * /api/integrations/lusha/config (the same endpoint the inline panel inside
 * Leads uses), so configuring here OR through a Lead enrichment first run
 * are equivalent.
 */
export default function LushaSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/integrations/lusha/config');
        const d = await r.json().catch(() => ({}));
        if (r.ok) {
          setConfig(d.config || null);
          setEnabled(d.config ? d.config.enabled !== false : true);
        }
      } finally { setLoading(false); }
    })();
  }, []);

  async function save() {
    setSaving(true);
    try {
      const body = { enabled };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const r = await apiFetch('/api/integrations/lusha/config', { method: 'PUT', body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Save failed'); return; }
      setConfig(d.config || null);
      setApiKey('');
      toast.success('Lusha settings saved');
    } finally { setSaving(false); }
  }

  async function test() {
    setTesting(true);
    try {
      const r = await apiFetch('/api/integrations/lusha/test', { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Test failed'); return; }
      toast.success(d.message || 'Lusha key works');
    } finally { setTesting(false); }
  }

  if (loading) return <div style={{ padding: 20, color: '#94A3B8' }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16 }}>Lusha — contact enrichment <StatusPill saved={!!config?.api_key_set} enabled={enabled} /></h2>
          <div style={{ color: '#94A3B8', fontSize: 12.5, marginTop: 4 }}>
            Looks up phone numbers, work emails, and job titles for leads. The same key drives the
            "Enrich from Lusha" button inside the Lead edit modal.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {config?.api_key_set && <button onClick={test} disabled={testing} style={btnSecondary}>{testing ? 'Testing…' : 'Test key'}</button>}
          <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <Section title="API key">
        <Field label="Lusha API key">
          <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} style={inp}
            placeholder={config?.api_key_set ? '•••••••• (saved — type to change)' : 'Paste your Lusha API key'} />
        </Field>
        <div style={{ fontSize: 11.5, color: '#64748B', marginTop: 8 }}>
          Find your key at <em>app.lusha.com → Settings → API</em>. Stored encrypted at rest with AES-256-GCM —
          the browser never sees the decrypted value once saved.
        </div>
      </Section>

      <Section title="Status">
        <Field label="Enabled">
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingTop: 6 }}>
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
            <span style={{ fontSize: 12.5, color: '#94A3B8' }}>Allow Lusha lookups from the app</span>
          </label>
        </Field>
      </Section>
    </div>
  );
}

// ─── Shared layout helpers ───────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}
function StatusPill({ saved, enabled }) {
  let label = 'Not configured', color = '#94A3B8', bg = 'rgba(148,163,184,0.15)', border = 'rgba(148,163,184,0.40)';
  if (saved && enabled)  { label = 'Configured · live';     color = '#10B981'; bg = 'rgba(16,185,129,0.15)'; border = 'rgba(16,185,129,0.40)'; }
  else if (saved)        { label = 'Configured · disabled'; color = '#F59E0B'; bg = 'rgba(245,158,11,0.15)'; border = 'rgba(245,158,11,0.40)'; }
  return (
    <span style={{
      marginLeft: 10, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
      color, background: bg, border: `1px solid ${border}`, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{label}</span>
  );
}

const inp = {
  padding: '8px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%',
};
const btnPrimary = {
  padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
  background: 'linear-gradient(135deg, #00E0FF, #3B8FF4)', color: '#fff',
};
const btnSecondary = {
  padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer',
};
